import { FetchResult } from '@apollo/client'
import { EnvelopeIcon, EnvelopeOpenIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { useRecoilState } from 'recoil'
import {
    ContentAction,
    ContentActionView,
    ContentView,
    TakeContentActionsMutation,
    useTakeContentActionsMutation
} from '../../api/client'
import { userState } from '../../store/DomainStore'
import BaseActionButton from '../actions/buttons/BaseActionButton'
import LikeButton from '../actions/buttons/LikeButton'
import { toastError, toastSuccess, toastWarning } from '../Notification'
import { isActionApplied } from './content-card/actions'

type Props = {
    selectedContent: Map<number, ContentView>
    onActionClick: (contentActions: ContentActionView[]) => void
    onClearSelectedContent: () => void
    onSelectAll: () => void
    onReadAll: () => void
    onUnreadAll: () => void
    showMarkAsReadLoadingIndicator: boolean
    showMarkAsUnreadLoadingIndicator: boolean
    onHideAll: () => void
    onUnhideAll: () => void
    showHideAllLoadingIndicator: boolean
    showUnhideAllLoadingIndicator: boolean
    actionCacheMap: Map<number, ContentActionView[]>
}

/**
 * Displays actions for the selected content.
 */
export default function SelectedContentBar(props: Props) {
    const {
        selectedContent,
        onClearSelectedContent,
        onActionClick,
        onSelectAll,
        onReadAll,
        onUnreadAll,
        showMarkAsReadLoadingIndicator,
        showMarkAsUnreadLoadingIndicator,
        onHideAll,
        onUnhideAll,
        showHideAllLoadingIndicator,
        showUnhideAllLoadingIndicator,
        actionCacheMap
    } = props
    const contentArray = Array.from(selectedContent.values())

    // We want to track the loading of each of these individually - so we create two separate mutation functions.
    const [hideContentsMutation, { loading: hideContentLoading }] = useTakeContentActionsMutation()
    const [unhideContentsMutation, { loading: unhideContentLoading }] =
        useTakeContentActionsMutation()

    const [selectedAll, setSelectedAll] = useState(false)
    const [user] = useRecoilState(userState)

    function handleBulkSelectorClick() {
        if (selectedAll) {
            onClearSelectedContent()
        } else {
            onSelectAll()
        }
        setSelectedAll(!selectedAll)
    }

    // We want the buttons to be disabled or enabled based on what is already selected. E.g. Disable the hide button if all items are already hidden.
    const allHidden =
        contentArray.filter((content) => {
            const allExistingContentActions = actionCacheMap.get(content.id) || []
            return isActionApplied(allExistingContentActions, ContentAction.Hide)
        }).length === contentArray.length

    const allUnhidden =
        contentArray.filter((content) => {
            const allExistingContentActions = actionCacheMap.get(content.id) || []
            return !isActionApplied(allExistingContentActions, ContentAction.Hide)
        }).length === contentArray.length

    const handleHideAllClicked = async () => {
        await toggleHide(true)
        await onHideAll()
    }

    const handleUnhideAllClicked = async () => {
        await toggleHide(false)
        await onUnhideAll()
    }

    async function toggleHide(shouldHide: boolean) {
        // Only hide/unhide if the content isn't already in the target state
        const contentToToggle = contentArray.filter(
            (content) => !(isActionApplied(content.actions, ContentAction.Hide) && shouldHide)
        )

        let response: FetchResult<TakeContentActionsMutation>

        if (shouldHide) {
            response = await hideContentsMutation({
                variables: {
                    contentIds: contentToToggle.map((content) => content.id),
                    action: ContentAction.Hide,
                    toggle: true,
                    baseUrl: `${window.location.protocol}//${window.location.host}/`
                },
                onCompleted: () => {
                    toastSuccess(
                        `Successfully hidden ${contentToToggle.length} comments. \n\n Refreshing content...`
                    )
                }
            })
        } else {
            response = await unhideContentsMutation({
                variables: {
                    contentIds: contentToToggle.map((content) => content.id),
                    action: ContentAction.Hide,
                    toggle: false,
                    baseUrl: `${window.location.protocol}//${window.location.host}/`
                },
                onCompleted: () => {
                    toastSuccess(
                        `Successfully unhidden ${contentToToggle.length} comments. \n\n Refreshing content...`
                    )
                }
            })
        }

        if (response.data?.takeContentActions?.contentActions?.length !== contentToToggle.length) {
            if (response.data?.takeContentActions?.contentActions?.length === 0) {
                toastError(
                    "Unable to hide any comments. \n\nThis can happen if content has been removed from the platform or Arwen's permission to act on your behalf has been revoked."
                )
            } else {
                // We can assume that some of these failed to hide.
                let successCount =
                    contentToToggle.length -
                    (contentToToggle.length -
                        (response.data?.takeContentActions?.contentActions?.length || 0))
                toastWarning(
                    `Successfully ${shouldHide ? 'hidden' : 'unhidden'} ${successCount} out of ${
                        contentArray.length
                    }. \n\nSome comments were not hidden - this can happen if content has been removed from the platform or Arwen's permission to act on your behalf has been revoked.`
                )
            }
        }
    }

    return (
        <>
            <div className="h-12 text-sm  flex items-center bg-gray-100  px-4 py-2 justify-between">
                <>
                    <div className="flex flex-row items-center ">
                        <div>
                            <input
                                id="admin"
                                aria-describedby="admin-description"
                                type="checkbox"
                                checked={selectedAll}
                                className="h-4 w-4 rounded  border-gray-300 text-accent-2-500 focus:ring-accent-2-600 "
                                onChange={handleBulkSelectorClick}
                            />
                        </div>

                        {/* 
                            Unlike with the ActionRow the buttons in this list do not represent a state, just a function. 
                            For example the hide button in the ActionRow can be clicked and it also changes to colour to indicate 
                            state. That doesn't happen with these buttons, primarily becuase they would have to represent the 
                            state of multiple items, which isn't possible. If we try it (and I did) you then have to decided what
                            to show and what the button does. It becomes very confusing. Separating them out is much much simpler 
                            to understand and pleasingly to code 
                        */}
                        <div className="ml-6 flex flex-row items-center gap-2 relative">
                            {user.canUseReadStatus && (
                                <>
                                    <div className="relative">
                                        <BaseActionButton
                                            tooltip={'Mark selected as read'}
                                            loading={showMarkAsReadLoadingIndicator}
                                            colour={'primary'}
                                            actionAlreadyApplied={false}
                                            icon={<EnvelopeOpenIcon />}
                                            onActionClick={onReadAll}
                                            isActionQueued={false}
                                            disabled={selectedContent.size === 0}
                                            disabledMessage={
                                                selectedContent.size === 0
                                                    ? 'Select comments to mark as read '
                                                    : ''
                                            }
                                        />
                                    </div>{' '}
                                    <div className="relative">
                                        <BaseActionButton
                                            tooltip={`Mark selected as unread`}
                                            loading={showMarkAsUnreadLoadingIndicator}
                                            colour={'primary'}
                                            actionAlreadyApplied={false}
                                            icon={<EnvelopeIcon />}
                                            onActionClick={onUnreadAll}
                                            isActionQueued={false}
                                            disabled={selectedContent.size === 0}
                                            disabledMessage={
                                                selectedContent.size === 0
                                                    ? 'Select comments to mark as unread'
                                                    : ''
                                            }
                                        />
                                    </div>
                                </>
                            )}
                            <>
                                <div className="relative">
                                    <BaseActionButton
                                        tooltip={'Hide selected comments'}
                                        loading={hideContentLoading || showHideAllLoadingIndicator}
                                        colour={'blue'}
                                        actionAlreadyApplied={false}
                                        icon={<EyeSlashIcon />}
                                        onActionClick={handleHideAllClicked}
                                        isActionQueued={false}
                                        disabled={selectedContent.size === 0 || allHidden}
                                        disabledMessage={
                                            selectedContent.size === 0 || allHidden
                                                ? 'Select comments to hide '
                                                : ''
                                        }
                                    />
                                </div>
                                <div className="relative">
                                    <BaseActionButton
                                        tooltip={'Unhide selected comments'}
                                        loading={
                                            unhideContentLoading || showUnhideAllLoadingIndicator
                                        }
                                        colour={'blue'}
                                        actionAlreadyApplied={false}
                                        icon={<EyeIcon />}
                                        onActionClick={handleUnhideAllClicked}
                                        isActionQueued={false}
                                        disabled={selectedContent.size === 0 || allUnhidden}
                                        disabledMessage={
                                            selectedContent.size === 0 || allUnhidden
                                                ? 'Select comments to unhide '
                                                : ''
                                        }
                                    />
                                </div>
                            </>
                            <div className="w-[1px] h-7 bg-gray-300"></div>
                            {user.canLikeContent && (
                                <>
                                    <LikeButton
                                        contents={contentArray}
                                        onActionClick={onActionClick}
                                        actions={[]}
                                        disabled={selectedContent.size === 0}
                                        disabledMessage={
                                            selectedContent.size === 0
                                                ? 'Select comments to like'
                                                : ''
                                        }
                                    />
                                </>
                            )}
                        </div>
                        <span className="text-gray-700 ml-2">{selectedContent.size} selected</span>
                        <div className="pl-4"></div>
                    </div>
                </>
            </div>
        </>
    )
}
